import { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { Button, Modal, Select, Table, TextInput, Grid, GridCol, Loader } from '@mantine/core';
import ModalComponent from '../../../components/modal';
import PaginationComponent from '../../../components/pagination';
import { LoginState } from '../../../client-redux/login/reducer';
import { AppState } from '../../../client-redux/reducers';
import { Language } from '../../../languages/languageHandler';
import { entityTypes } from '../../utils';
import { fetchUsers, IUser } from '../../../../libraries/user';
import { IEntity } from '../../../../libraries/entity';
import { isZarphSuccessResponse, useStatusOperDialog } from '../../../context/status-oper-dialog';
import './styles.scss';

interface ModalProps {
  isOpen: boolean;
  hideBtn: () => void;
  lang: Language;
  chooseUser: (user: IUser) => void;
}

const ChooseUserModal = ({ isOpen, hideBtn, lang, chooseUser }: ModalProps) => {
  const loginInfo = useSelector<AppState, LoginState>(state => state.login);
  const controller = useRef(new AbortController()).current;
  const entities = useSelector<AppState, IEntity[]>(state => state.entities.entities);
  const tableWrapperRef = useRef<HTMLDivElement>(null);

  const [entid, setEntid] = useState<number>(0);
  const [name, setName] = useState<string>('');
  const [uid] = useState<number>();
  const [, setId] = useState<number>();
  const [offset, setOffset] = useState<number>(0);
  const [limit, setLimit] = useState<number>(10);
  const [totalRows, setTotalRows] = useState<number>(0);
  const [message, setMessage] = useState<IUser[]>([]);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [, setLastSelectedPage] = useState<number>(1);
  const [firstLoad, setFirstLoad] = useState<boolean>(true);

  const { handleApiError } = useStatusOperDialog();

  useEffect(() => {
    if (firstLoad) {
      setFirstLoad(false);
    } else {
      getUsersList();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [offset, limit, currentPage]);

  const getUsersList = () => {
    if (!loginInfo.token) return;

    setLoading(true);
    setMessage([]);
    setTotalRows(0);

    fetchUsers(controller, entid, limit, offset, true, name, uid)
      .then(response => {
        if (isZarphSuccessResponse(response)) {
          setMessage(response.data.users || []);
          setTotalRows(response.data.totalRows || 0);
        } else {
          handleApiError(response, lang.ALERT_ERROR_FETCHING_DATA);
        }
      })
      .catch(err => {
        handleApiError(err, lang.ALERT_ERROR_FETCHING_DATA);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const changePage = (page: number) => {
    const newOffset = (page - 1) * limit;
    setCurrentPage(page);
    setOffset(newOffset);
    setLastSelectedPage(page);
  };

  const resultsSelect = (newValue: number) => {
    const newLimit = Number(newValue);
    setLimit(newLimit);
    setCurrentPage(0);
    setOffset(0);
    setCurrentPage(1);
  };

  /* Entity handle*/
  const handleEntitySelect = (value: number | 0) => {
    if (value !== null) {
      const selectedEntid = Number(value);
      setEntid(selectedEntid);
    }
  };

  /* Name handle*/
  const handleTableByName = (value: string) => {
    if (value !== null) {
      const selectedName = String(value);
      setName(selectedName);
    }
  };

  /* ID  handle*/
  const handleTableByID = (value: number) => {
    if (value !== null) {
      const selectedID = Number(value);
      setId(selectedID);
    }
  };

  const handleEntitySelection = (user: IUser) => {
    chooseUser(user);
    hideBtn();
  };

  //Allow for enter key to search
  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      getUsersList();
    }
  };
  return (
    <ModalComponent
      show={isOpen}
      handleClick={hideBtn}
      title={lang.HELPDESK_CHOOSE_ENTITY}
      classname="choose-user-modal"
      content={
        <Modal.Body style={{ margin: 0, padding: 0 }}>
          <Grid classNames={{ root: 'choose-distribuition-user' }}>
            <GridCol span={6}>
              <Select
                size="sm"
                withCheckIcon
                label={lang.GLOBAL_ENTITY}
                data={
                  entities?.map(entity => ({
                    value: entity.entid.toString(),
                    label: entity.name
                  })) ?? []
                }
                value={entid.toString()}
                onChange={value => handleEntitySelect(Number(value))}
              />
            </GridCol>

            <GridCol span={6}>
              <TextInput
                label={lang.GLOBAL_NAME}
                onChange={event => handleTableByName(event.target.value)}
                onKeyDown={handleKeyDown}
              />
            </GridCol>

            <GridCol span={6}>
              <Select
                label={lang.USER_UID}
                data={entityTypes(lang)}
                placeholder={lang.GLOBAL_ALL}
                onChange={value => handleTableByID(Number(value))}
              />
            </GridCol>

            <GridCol span={6}>
              <Button radius="md" onClick={getUsersList}>
                {lang.GLOBAL_SEARCH}
              </Button>
            </GridCol>

            <GridCol span={12}>
              <div ref={tableWrapperRef}>
                <Table.ScrollContainer minWidth={500}>
                  <Table className="distribuition-table" striped highlightOnHover>
                    <Table.Thead>
                      <Table.Tr>
                        <Table.Th>Entid</Table.Th>
                        <Table.Th>{lang.GLOBAL_NAME}</Table.Th>
                      </Table.Tr>
                    </Table.Thead>

                    <Table.Tbody>
                      {loading ? (
                        <Table.Tr>
                          <Table.Td colSpan={2}>
                            <Loader color="#016273" />
                          </Table.Td>
                        </Table.Tr>
                      ) : message.length === 0 ? (
                        <Table.Tr>
                          <Table.Td colSpan={2} style={{ textAlign: 'center' }}>
                            {lang.GLOBAL_NO_RESULTS}
                          </Table.Td>
                        </Table.Tr>
                      ) : (
                        message.map((user, index) => (
                          <Table.Tr
                            key={index}
                            onClick={() => handleEntitySelection(user)}
                            style={{ cursor: 'pointer' }}
                          >
                            <Table.Td>{user.entid}</Table.Td>
                            <Table.Td>{user.name}</Table.Td>
                          </Table.Tr>
                        ))
                      )}
                    </Table.Tbody>
                  </Table>
                </Table.ScrollContainer>
              </div>
            </GridCol>

            {totalRows > 0 && (
              <GridCol span={12}>
                <PaginationComponent
                  total={totalRows}
                  itemsPerPage={limit}
                  dynamicLimit={limit}
                  onPageChange={changePage}
                  onResultsChange={resultsSelect}
                />
              </GridCol>
            )}
          </Grid>
        </Modal.Body>
      }
    />
  );
};

export default ChooseUserModal;
